<script setup lang="ts">
import { ref, onMounted } from 'vue';
import api from '@/services/api';

// --- User Management State ---
const users = ref([]);
const isUserModalActive = ref(false);
const isUserEditing = ref(false);
const userForm = ref({ id: null, username: '', password: '', firstname: '', lastname: '', role: 'User' });

// --- Call Destinations State ---
const destinations = ref([]);
const isDestModalActive = ref(false);
const isDestEditing = ref(false);
const destForm = ref({ id: null, name: '', number: '' });

// --- Fetch Data ---
const fetchData = async () => {
  const [userRes, destRes] = await Promise.all([
    api.get('/api/admin/users'),
    api.get('/api/admin/destinations') // Adjust endpoint as needed
  ]);
  users.value = userRes.data;
  destinations.value = destRes.data;
};

// --- User Methods ---
const openAddUserModal = () => {
  isUserEditing.value = false;
  userForm.value = { id: null, username: '', password: '', firstname: '', lastname: '', role: 'User' };
  isUserModalActive.value = true;
};

const openEditUserModal = (user: any) => {
  isUserEditing.value = true;
  userForm.value = { ...user, password: '' };
  isUserModalActive.value = true;
};

const saveUser = async () => {
  try {
    if (isUserEditing.value) {
      const payload: any = { ...userForm.value };
      if (!payload.password) delete payload.password;
      await api.patch(`/api/admin/users/${userForm.value.id}`, payload);
    } else {
      await api.post('/api/admin/users', userForm.value);
    }
    isUserModalActive.value = false;
    fetchData();
  } catch (err) { alert("Error saving user."); }
};

const deleteUser = async (id: number) => {
  if (confirm("Delete this user?")) {
    await api.delete(`/api/admin/users/${id}`);
    fetchData();
  }
};

// --- Destination Methods ---
const openAddDestModal = () => {
  isDestEditing.value = false;
  destForm.value = { id: null, name: '', number: '' };
  isDestModalActive.value = true;
};

const openEditDestModal = (dest: any) => {
  isDestEditing.value = true;
  destForm.value = { ...dest };
  isDestModalActive.value = true;
};

const saveDestination = async () => {
  try {
    if (isDestEditing.value) {
      await api.patch(`/api/admin/destinations/${destForm.value.id}`, destForm.value);
    } else {
      await api.post('/api/admin/destinations', destForm.value);
    }
    isDestModalActive.value = false;
    fetchData();
  } catch (err) { alert("Error saving destination."); }
};

const deleteDestination = async (id: number) => {
  if (confirm("Delete this destination?")) {
    await api.delete(`/api/admin/destinations/${id}`);
    fetchData();
  }
};

onMounted(fetchData);
</script>

<template>
  <div class="container is-fluid mt-5">
    
    <!-- User Management Section -->
    <div class="level">
      <div class="level-left"><h1 class="title">User Management</h1></div>
      <div class="level-right">
        <button class="button is-link" @click="openAddUserModal">
          <span class="icon"><font-awesome-icon icon="plus" /></span>
          <span>Add New User</span>
        </button>
      </div>
    </div>

    <div class="card mb-6">
      <div class="card-content">
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr><th>ID</th><th>Name</th><th>Username</th><th>Role</th><th>Actions</th></tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id">
                <td>{{ u.id }}</td>
                <td>{{ u.firstname }} {{ u.lastname }}</td>
                <td>{{ u.username }}</td>
                <td><span class="tag is-light">{{ u.role }}</span></td>
                <td>
                  <div class="buttons are-small">
                    <button class="button is-warning is-light" @click="openEditUserModal(u)">Edit</button>
                    <button class="button is-danger is-light" @click="deleteUser(u.id)">Delete</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="level">
      <div class="level-left"><h1 class="title">Connection Destinations</h1></div>
      <div class="level-right">
        <button class="button is-link" @click="openAddDestModal">
          <span class="icon"><font-awesome-icon icon="plus" /></span>
          <span>Add Destination</span>
        </button>
      </div>
    </div>

    <div class="card">
      <div class="card-content">
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr><th>Name</th><th>Number</th><th>Actions</th></tr>
            </thead>
            <tbody>
              <tr v-for="d in destinations" :key="d.id">
                <td><strong>{{ d.name }}</strong></td>
                <td>{{ d.number }}</td>
                <td>
                  <div class="buttons are-small">
                    <button class="button is-warning is-light" @click="openEditDestModal(d)">Edit</button>
                    <button class="button is-danger is-light" @click="deleteDestination(d.id)">Delete</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- User Modal -->
    <div class="modal" :class="{ 'is-active': isUserModalActive }">
      <div class="modal-background" @click="isUserModalActive = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ isUserEditing ? 'Edit User' : 'Add New User' }}</p>
        </header>
        <section class="modal-card-body">
          <form autocomplete="disabled-for-security">
            <div class="columns is-multiline">
              <div class="column is-6"><label class="label">First Name</label><input class="input" v-model="userForm.firstname"></div>
              <div class="column is-6"><label class="label">Last Name</label><input class="input" v-model="userForm.lastname"></div>
              <div class="column is-12"><label class="label">Username</label><input class="input" v-model="userForm.username" autocomplete="username"></div>
              <div class="column is-12"><label class="label">Password</label><input class="input" type="password" v-model="userForm.password" autocomplete="new-password"></div>
              <div class="column is-12"><label class="label">Role</label>
                <div class="select is-fullwidth"><select v-model="userForm.role"><option>User</option><option>Admin</option></select></div>
              </div>
            </div>
          </form>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="saveUser">Save</button>
          <button class="button" @click="isUserModalActive = false">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Destination Modal -->
    <div class="modal" :class="{ 'is-active': isDestModalActive }">
      <div class="modal-background" @click="isDestModalActive = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ isDestEditing ? 'Edit Destination' : 'Add Destination' }}</p>
        </header>
        <section class="modal-card-body">
          <form autocomplete="disabled-for-security">
            <div class="field">
              <label class="label">Destination Name</label>
              <input class="input" v-model="destForm.name" placeholder="e.g. Tieline 1">
            </div>
            <div class="field">
              <label class="label">Phone Number</label>
              <input class="input" v-model="destForm.number" placeholder="e.g. 1234">
            </div>
          </form>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="saveDestination">Save</button>
          <button class="button" @click="isDestModalActive = false">Cancel</button>
        </footer>
      </div>
    </div>

  </div>
</template>
